import React, { useState, useMemo, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useEstateContext } from "@/lib/EstateContext";
import { useSubscription } from "@/hooks/useSubscription";
import { TrialBanner } from "@/components/TrialBanner";
import PaymentIssueBanner from "@/components/PaymentIssueBanner";
import { estatesService, contractorsService } from "@/services";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Navigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Building2,
  Home,
  AlertCircle,
  Camera,
  LayoutDashboard,
  CheckCircle2,
  Clock,
  TrendingUp,
  Download,
  Filter,
  Calendar,
  Loader2,
  BarChart3,
  ArrowLeft,
  FileText,
  Plus,
  Settings,
  Tag,
  Users,
  Mail,
  Phone,
  Briefcase,
  Trash2,
  UserPlus,
  Upload,
  Smartphone,
  Eye,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import InternalIssueForm from "@/components/InternalIssueForm";
import InternalIssueDetail from "@/components/InternalIssueDetail";
import { captureEvent } from "@/lib/analytics";
import { useEstatePresence } from "@/hooks/useEstatePresence";
import { PresenceAvatars } from "@/components/PresenceAvatars";

export default function EstateDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeEstateId, myEstates, isLoadingEstates, activeEstate } = useEstateContext();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();

  const [activeTab, setActiveTab] = useState("internal");
  const [showInternalForm, setShowInternalForm] = useState(false);
  const [selectedInternalIssue, setSelectedInternalIssue] = useState(null);
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [contractorForm, setContractorForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    specialty: "",
  });
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [localLogoUrl, setLocalLogoUrl] = useState(null);
  const logoInputRef = useRef(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Realtime presence — must come after activeTab is declared
  const presenceList = useEstatePresence(activeEstateId, user?.id);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const newUrl = await estatesService.uploadLogo(activeEstateId, file);
      setLocalLogoUrl(newUrl); // show instantly without waiting for query refetch
      queryClient.invalidateQueries({ queryKey: ["estate", activeEstateId] });
      toast.success("Estate logo updated!");
    } catch (err) {
      console.error('Logo upload error:', err);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const handleGenerateInvite = async () => {
    setInviteLoading(true);
    try {
      const result = await estatesService.createInvite(activeEstateId, user.id);
      setInviteData(result);
      setShowInviteModal(true);
    } catch (err) {
      console.error('Invite generation error:', err);
      toast.error("Failed to generate invite link. Please try again.");
    } finally {
      setInviteLoading(false);
    }
  };

  const isNativeContactPickerSupported =    typeof navigator !== "undefined" &&
    !!navigator.contacts &&
    typeof navigator.contacts.select === "function";
  
  const queryClient = useQueryClient();
  
  const [filters, setFilters] = useState({
    status: "",
    unit_id: "",
    start_date: "",
    end_date: "",
  });
  
  const [internalFilters, setInternalFilters] = useState({
    status: "all",
    category: "all", 
    assigned_to: "all",
    start_date: "",
    end_date: "",
  });

  const isEnabled = !!activeEstateId && user?.user_role === "estate_manager";

  // Fetch estate details
  const { data: estate, isLoading: estateLoading } = useQuery({
    queryKey: ["estate", activeEstateId],
    queryFn: () => estatesService.getById(activeEstateId),
    enabled: isEnabled,
  });

  // Fetch estate units
  const { data: units = [], isLoading: unitsLoading } = useQuery({
    queryKey: ["estate-units", activeEstateId],
    queryFn: () => estatesService.getUnits(activeEstateId),
    enabled: isEnabled,
  });

  // Fetch estate reports with filters
  const { data: reports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ["estate-reports", activeEstateId, filters],
    queryFn: () => estatesService.getEstateReports(activeEstateId, filters),
    enabled: isEnabled,
  });

  // Fetch estate statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["estate-stats", activeEstateId],
    queryFn: () => estatesService.getEstateStats(activeEstateId),
    enabled: isEnabled,
  });

  // Fetch internal issues with filters
  const { data: internalIssues = [], isLoading: internalIssuesLoading } = useQuery({
    queryKey: ["internal-issues", activeEstateId, internalFilters],
    queryFn: () => estatesService.getInternalIssues(activeEstateId, internalFilters),
    enabled: isEnabled,
  });

  // Fetch internal issues statistics
  const { data: internalStats, isLoading: internalStatsLoading } = useQuery({
    queryKey: ["internal-issues-stats", activeEstateId],
    queryFn: () => estatesService.getInternalIssuesStats(activeEstateId),
    enabled: isEnabled,
  });

  // Fetch contractors for this estate
  const { data: contractors = [], isLoading: contractorsLoading } = useQuery({
    queryKey: ["contractors", activeEstateId],
    queryFn: () => contractorsService.listByEstate(activeEstateId),
    enabled: isEnabled,
  });

  // Contractor mutations
  const createContractorMutation = useMutation({
    mutationFn: (data) => contractorsService.create(activeEstateId, data),
    onSuccess: (createdContractor) => {
      captureEvent("contractor_created", {
        contractor_id: createdContractor?.id,
        specialty: createdContractor?.specialty,
        has_company: !!createdContractor?.company,
      });
      queryClient.invalidateQueries({ queryKey: ["contractors", activeEstateId] });
      toast.success("Contractor added successfully");
      setShowContractorModal(false);
      setContractorForm({ name: "", email: "", phone: "", company: "", specialty: "" });
    },
    onError: (error) => {
      console.error('Add contractor error:', error);
      toast.error("Failed to add contractor. Please try again.");
    },
  });

  const updateContractorMutation = useMutation({
    mutationFn: ({ id, data }) => contractorsService.update(id, data),
    onSuccess: (updatedContractor) => {
      captureEvent("contractor_updated", {
        contractor_id: updatedContractor?.id,
        specialty: updatedContractor?.specialty,
        has_company: !!updatedContractor?.company,
      });
      queryClient.invalidateQueries({ queryKey: ["contractors", activeEstateId] });
      toast.success("Contractor updated successfully");
      setShowContractorModal(false);
      setContractorForm({ name: "", email: "", phone: "", company: "", specialty: "" });
    },
    onError: (error) => {
      console.error('Update contractor error:', error);
      toast.error("Failed to update contractor. Please try again.");
    },
  });

  const deleteContractorMutation = useMutation({
    mutationFn: (id) => contractorsService.delete(id),
    onSuccess: (_, contractorId) => {
      captureEvent("contractor_deleted", {
        contractor_id: contractorId,
      });
      queryClient.invalidateQueries({ queryKey: ["contractors", activeEstateId] });
      toast.success("Contractor deleted successfully");
    },
    onError: (error) => {
      console.error('Delete contractor error:', error);
      toast.error("Failed to delete contractor. Please try again.");
    },
  });

  const normalizePhone = (phone) => (phone || "").replace(/[^\d+]/g, "").trim();
  const normalizeName = (name) => (name || "").trim().toLowerCase();

  const parseCSVLine = (line) => {
    const values = [];
    let current = "";
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const next = line[i + 1];

      if (char === '"') {
        if (insideQuotes && next === '"') {
          current += '"';
          i++;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        values.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values.map((value) => value.replace(/^"|"$/g, "").trim());
  };

  const getCSVValue = (row, headerIndex, aliases) => {
    const headerKey = Object.keys(headerIndex).find((key) => aliases.includes(key));
    if (!headerKey) return "";
    return row[headerIndex[headerKey]] || "";
  };

  const generateFallbackEmail = (name, index) => {
    const safeName = (name || "contractor").toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 20) || "contractor";
    return `${safeName}.${Date.now()}.${index}@fixmyhood.local`;
  };

  const findDuplicateContractor = (row) => {
    const phone = normalizePhone(row.phone);
    const name = normalizeName(row.name);

    if (phone) {
      const byPhone = contractors.find((contractor) => normalizePhone(contractor.phone) === phone);
      if (byPhone) return byPhone;
    }

    if (name) {
      const byName = contractors.find((contractor) => normalizeName(contractor.name) === name);
      if (byName) return byName;
    }

    return null;
  };

  const buildImportRows = (rawRows = []) => {
    return rawRows
      .map((row, index) => {
        const name = (row.name || "").trim();
        const phone = (row.phone || "").trim();
        const email = (row.email || "").trim();
        const company = (row.company || "").trim();
        const specialty = (row.specialty || "").trim();

        const fallbackName = name || (phone ? `Contractor ${phone.slice(-4) || index + 1}` : "");
        if (!fallbackName) return null;

        const duplicateContractor = findDuplicateContractor({ name: fallbackName, phone });

        return {
          id: `${Date.now()}-${index}-${fallbackName}-${phone}`,
          name: fallbackName,
          phone,
          email: email || generateFallbackEmail(fallbackName || "contractor", index + 1),
          company,
          specialty: specialty || "General",
          duplicateContractor,
          action: duplicateContractor ? "skip" : "create",
          isValid: true,
        };
      })
      .filter(Boolean);
  };

  const handleCSVFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    try {
      const csvText = await file.text();
      const lines = csvText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.length < 2) {
        toast.error("CSV must include a header row and at least one contact row");
        return;
      }

      const headers = parseCSVLine(lines[0]).map((header) => header.toLowerCase().replace(/\s+/g, "_"));
      const headerIndex = headers.reduce((acc, header, index) => {
        acc[header] = index;
        return acc;
      }, {});

      const csvRows = lines.slice(1).map((line) => {
        const row = parseCSVLine(line);
        const name = getCSVValue(row, headerIndex, ["name", "full_name", "display_name", "first_name"]);
        const phone = getCSVValue(row, headerIndex, ["phone", "mobile", "mobile_phone", "phone_1", "phone_number"]);
        const email = getCSVValue(row, headerIndex, ["email", "email_address", "e_mail_1_-_value"]);
        const company = getCSVValue(row, headerIndex, ["company", "organization", "company_name"]);
        const specialty = getCSVValue(row, headerIndex, ["specialty", "trade", "category"]);
        return {
          name,
          phone,
          email,
          company,
          specialty,
        };
      });

      const parsedRows = buildImportRows(csvRows);

      if (parsedRows.length === 0) {
        toast.error("No valid contractor rows found in CSV");
        return;
      }

      setImportRows(parsedRows);
      setImportFileName(file.name);
      captureEvent("contractor_import_file_loaded", {
        source: "csv",
        rows_loaded: parsedRows.length,
      });
      toast.success(`Loaded ${parsedRows.length} contacts from CSV`);
    } catch (error) {
      console.error("CSV parsing failed:", error);
      toast.error("Failed to parse CSV file");
    } finally {
      event.target.value = "";
    }
  };

  const handleImportFromDeviceContacts = async () => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Subscription required", {
        description: "Upgrade to import contractors"
      });
      return;
    }

    if (!isNativeContactPickerSupported) {
      captureEvent("contractor_import_source_fallback", {
        source: "device_contacts",
        fallback: "csv",
      });
      toast.info("Direct contacts import is not supported on this device/browser. Please use CSV import.");
      setShowImportModal(true);
      return;
    }

    try {
      const selectedContacts = await navigator.contacts.select(["name", "tel", "email"], { multiple: true });

      const normalizedContacts = (selectedContacts || []).map((contact) => ({
        name: Array.isArray(contact.name) ? (contact.name[0] || "") : (contact.name || ""),
        phone: Array.isArray(contact.tel) ? (contact.tel[0] || "") : (contact.tel || ""),
        email: Array.isArray(contact.email) ? (contact.email[0] || "") : (contact.email || ""),
        company: "",
        specialty: "General",
      }));

      const parsedRows = buildImportRows(normalizedContacts);

      if (parsedRows.length === 0) {
        toast.error("No valid contacts selected");
        return;
      }

      setImportRows(parsedRows);
      setImportFileName("Device Contacts");
      setShowImportModal(true);
      captureEvent("contractor_import_file_loaded", {
        source: "device_contacts",
        rows_loaded: parsedRows.length,
      });
      toast.success(`Loaded ${parsedRows.length} contacts from device`);
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }
      captureEvent("contractor_import_device_failed", {
        error: error?.message,
      });
      console.error("Device contacts import failed:", error);
      toast.error("Failed to import contacts from this device");
    }
  };

  const handleImportActionChange = (rowId, action) => {
    setImportRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, action } : row))
    );
  };

  const resetImportState = () => {
    setImportRows([]);
    setImportFileName("");
    setShowImportModal(false);
  };

  const handleImportContractors = async () => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Subscription required", {
        description: "Upgrade to import contractors"
      });
      return;
    }

    if (importRows.length === 0) {
      toast.error("Please upload a CSV file first");
      return;
    }

    setIsImporting(true);
    let created = 0;
    let updated = 0;
    let skipped = 0;
    let failed = 0;

    try {
      for (const row of importRows) {
        try {
          if (row.action === "skip") {
            skipped++;
            continue;
          }

          if (row.action === "update" && row.duplicateContractor) {
            await contractorsService.update(row.duplicateContractor.id, {
              name: row.name || row.duplicateContractor.name,
              email: row.email || row.duplicateContractor.email,
              phone: row.phone || row.duplicateContractor.phone,
              company: row.company || row.duplicateContractor.company,
              specialty: row.specialty || row.duplicateContractor.specialty || "General",
            });
            updated++;
            continue;
          }

          await contractorsService.create(activeEstateId, {
            name: row.name,
            email: row.email,
            phone: row.phone,
            company: row.company,
            specialty: row.specialty || "General",
          });
          created++;
        } catch (error) {
          console.error("Import row failed:", row, error);
          failed++;
        }
      }

      queryClient.invalidateQueries({ queryKey: ["contractors", activeEstateId] });
      captureEvent("contractor_import_completed", {
        created,
        updated,
        skipped,
        failed,
      });
      toast.success(`Import complete: ${created} added, ${updated} updated, ${skipped} skipped${failed ? `, ${failed} failed` : ""}`);
      resetImportState();
    } finally {
      setIsImporting(false);
    }
  };

  // Calculate top units
  const topUnits = useMemo(() => {
    if (!stats?.topUnits || !units.length) return [];
    
    return Object.entries(stats.topUnits)
      .map(([unitId, count]) => ({
        unit: units.find(u => u.id === unitId),
        count,
      }))
      .filter(item => item.unit)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [stats, units]);

  // Prepare chart data
  const unitChartData = useMemo(() => {
    if (!stats?.topUnits || !units.length) return [];
    
    return Object.entries(stats.topUnits)
      .map(([unitId, count]) => ({
        unit: units.find(u => u.id === unitId)?.unit_number || "Unknown",
        issues: count,
      }))
      .sort((a, b) => b.issues - a.issues)
      .slice(0, 10);
  }, [stats, units]);

  const trendChartData = useMemo(() => {
    if (!stats?.monthlyTrend) return [];
    
    return Object.entries(stats.monthlyTrend)
      .map(([month, count]) => ({
        month,
        issues: count,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [stats]);

  // If no user, return null — auth system handles the redirect to /Login
  if (!user) return null;
  // Redirect non-managers away from this page
  if (user.user_role !== "estate_manager") {
    return <Navigate to="/ReportIssue" replace />;
  }

  // Check if user has no estates (multi-estate aware)
  if (user?.user_role === "estate_manager" && !isLoadingEstates && myEstates.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-[#1883FF] mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4 text-slate-900">No Estate Assigned</h2>
          <p className="text-slate-600 mb-6">
            You don't manage any estates yet. Create one to get started.
          </p>
          <Button
            onClick={() => navigate(createPageUrl("CreateEstate"))}
            className="w-full bg-[#1883FF] hover:bg-[#0066CC] rounded-[24px] md:rounded-[40px]"
          >
            Create Estate
          </Button>
        </Card>
      </div>
    );
  }

  const handleExportCSV = async () => {
    if (!subscription?.hasWriteAccess) {
      toast.error("Please update your payment method to export data");
      return;
    }
    
    try {
      toast.loading("Preparing CSV export...");
      await estatesService.exportToCSV(activeEstateId);
      captureEvent("estate_csv_exported", {
        estate_id: activeEstateId,
      });
      toast.dismiss();
      toast.success("CSV exported successfully!");
    } catch (error) {
      captureEvent("estate_csv_export_failed", {
        estate_id: activeEstateId,
        error: error?.message,
      });
      toast.dismiss();
      toast.error("Failed to export CSV. Please try again.");
      console.error("Export error:", error);
    }
  };

  const clearFilters = () => {
    if (activeTab === "community") {
      setFilters({
        status: "",
        unit_id: "",
        start_date: "",
        end_date: "",
      });
    } else {
      setInternalFilters({
        status: "all",
        category: "all",
        assigned_to: "all", 
        start_date: "",
        end_date: "",
      });
    }
  };

  const handleInternalIssueClick = (issue) => {
    captureEvent("internal_issue_detail_opened", {
      issue_id: issue?.id,
      status: issue?.status,
      category: issue?.category,
    });
    setSelectedInternalIssue(issue);
  };

  if (estateLoading || !estate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F0F0] dark:bg-gray-900">
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#1883FF' }} />
      </div>
    );
  }

  const isLoading = reportsLoading || statsLoading || unitsLoading;
  const hasActiveFilters = filters.status || filters.unit_id || filters.start_date || filters.end_date;

  const handleUpgrade = () => {
    navigate(createPageUrl("UpgradePage"));
  };

  return (
    <div className="min-h-screen p-4 md:p-8 bg-[#F0F0F0] dark:bg-gray-900">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Payment Issue Banner */}
        <PaymentIssueBanner subscription={subscription} />
        
        {/* Trial Banner */}
        {subscription && !bannerDismissed && (
          <TrialBanner
            subscription={subscription}
            onUpgrade={handleUpgrade}
            onDismiss={() => setBannerDismissed(true)}
          />
        )}
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            {/* Estate logo / upload widget */}
            <div className="hidden sm:block">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      className="relative w-12 h-12 rounded-xl overflow-hidden border-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex items-center justify-center group hover:border-[#1883FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1883FF]"
                      aria-label="Upload estate logo"
                    >
                      {logoUploading ? (
                        <Loader2 className="w-5 h-5 animate-spin text-[#1883FF]" />
                      ) : (localLogoUrl || estate.logo_url) ? (
                        <>
                          <img
                            src={localLogoUrl || estate.logo_url}
                            alt="Estate logo"
                            className="w-full h-full object-contain p-1"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Camera className="w-4 h-4 text-white" />
                          </div>
                        </>
                      ) : (
                        <>
                          <Building2 className="w-6 h-6 text-[#1883FF]" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
                            <Camera className="w-4 h-4 text-white" />
                          </div>
                        </>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{(localLogoUrl || estate.logo_url) ? "Change estate logo" : "Upload estate logo"}</p>
                    <p className="text-xs text-slate-400">Shows on monthly reports</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {/* Back button removed - MapView not relevant for Estate Managers */}
            <div>
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                {estate.name}
              </h1>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
                Estate Management Dashboard
              </p>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-col sm:flex-row gap-2">
          {/* Invite Manager — only shown to estate owners */}
          {activeEstate?.member_role === 'admin' && (
            <Button
              onClick={handleGenerateInvite}
              disabled={inviteLoading}
              variant="outline"
              className="rounded-[24px] md:rounded-[40px] w-full sm:w-auto border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-950 dark:hover:text-green-300 dark:active:bg-green-900"
            >
              {inviteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Invite Manager
            </Button>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full sm:w-auto">
                  <Button
                    onClick={() => navigate(`${createPageUrl("MonthlyReport")}?estate_id=${activeEstateId}`)}
                    className="bg-[#1883FF] hover:bg-[#0066CC] text-white rounded-[24px] md:rounded-[40px] w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={subscription?.isExpired}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Monthly Report
                  </Button>
                </div>
              </TooltipTrigger>
              {subscription?.isExpired && (
                <TooltipContent>
                  <p>Subscription expired - renew to access reports</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-full sm:w-auto">
                  <Button
                    onClick={handleExportCSV}
                    className="bg-[#1883FF] hover:bg-[#0066CC] rounded-[24px] md:rounded-[40px] w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!subscription?.hasWriteAccess || isLoading}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </TooltipTrigger>
              {!subscription?.hasWriteAccess && (
                <TooltipContent>
                  <p>Update payment to export data</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          </div>
          <div className="hidden sm:block">
            <PresenceAvatars presenceList={presenceList} />
          </div>
        </div>
      </motion.div>        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-2 sm:gap-1 p-1 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-slate-700"
        >
          <button
            onClick={() => setActiveTab("internal")}
            className={`px-4 py-2 rounded-[24px] md:rounded-[40px] text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "internal"
                ? "bg-[#1883FF] text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            <AlertCircle className="w-4 h-4" />
            <span className="whitespace-nowrap">Internal Issues</span>
          </button>
          <button
            onClick={() => setActiveTab("contractors")}
            className={`px-4 py-2 rounded-[24px] md:rounded-[40px] text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "contractors"
                ? "bg-[#1883FF] text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            <Users className="w-4 h-4" />
            <span className="whitespace-nowrap">Contractors</span>
          </button>
          <button
            onClick={() => setActiveTab("community")}
            className={`px-4 py-2 rounded-[24px] md:rounded-[40px] text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === "community"
                ? "bg-[#1883FF] text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            <Eye className="w-4 h-4" />
            <span className="whitespace-nowrap">Estate Overview</span>
          </button>
        </motion.div>

        {/* Tab Content */}
        {activeTab === "community" && (
          <>
            {/* KPI Cards for Estate Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Open Issues</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                    {statsLoading ? "..." : stats?.totalOpen || 0}
                  </p>
                </div>
                <div className="p-3 md:p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertCircle className="w-6 h-6 md:w-8 md:h-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">New This Month</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                    {statsLoading ? "..." : stats?.newThisMonth || 0}
                  </p>
                </div>
                <div className="p-3 md:p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Resolved This Month</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                    {statsLoading ? "..." : stats?.resolvedThisMonth || 0}
                  </p>
                </div>
                <div className="p-3 md:p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Avg Resolution</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                    {statsLoading ? "..." : stats?.averageResolutionTime || 0}
                    <span className="text-base font-normal text-slate-600 dark:text-slate-400 ml-1">days</span>
                  </p>
                </div>
                <div className="p-3 md:p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                  <Clock className="w-6 h-6 md:w-8 md:h-8 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Top Units */}
        {topUnits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <Home className="w-5 h-5 text-[#1883FF]" />
                Top 5 Units by Reports
              </h2>
              <div className="space-y-3">
                {topUnits.map((item, idx) => (
                  <div
                    key={item.unit.id}
                    className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center font-bold text-[#1883FF] dark:text-[#1883FF]">
                        {idx + 1}
                      </div>
                      <span className="font-medium text-slate-900 dark:text-white">
                        Unit {item.unit.unit_number}
                      </span>
                    </div>
                    <span className="text-slate-600 dark:text-slate-400 font-semibold">
                      {item.count} {item.count === 1 ? "issue" : "issues"}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Issues by Unit */}
          {unitChartData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#1883FF]" />
                  Issues per Unit
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={unitChartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="unit" />
                    <YAxis />
                    <ChartTooltip />
                    <Bar dataKey="issues" fill="#ea580c" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </motion.div>
          )}

          {/* Trend Over Time */}
          {trendChartData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#1883FF]" />
                  6-Month Trend
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip />
                    <Line type="monotone" dataKey="issues" stroke="#ea580c" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Filter className="w-5 h-5 text-[#1883FF]" />
                Filter Reports
              </h2>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-600 dark:text-white border-gray-300 dark:border-white hover:bg-gray-100 dark:hover:bg-white dark:hover:text-slate-900"
                >
                  Clear Filters
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}>
                  <SelectTrigger className="dark:border-white dark:text-white">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="reported">Reported</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={filters.unit_id || 'all'} onValueChange={(value) => setFilters({ ...filters, unit_id: value === 'all' ? '' : value })}>
                  <SelectTrigger className="dark:border-white dark:text-white">
                    <SelectValue placeholder="All units" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All units</SelectItem>
                    {(units || []).map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        Unit {unit.unit_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="dark:border-white dark:text-white dark:[color-scheme:dark]"
                />
              </div>
              
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="dark:border-white dark:text-white dark:[color-scheme:dark]"
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Reports Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
              Estate Reports ({reports.length})
            </h2>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400">
                  {hasActiveFilters ? "No reports match your filters" : "No reports yet"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Unit
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Issue Type
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Created
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Resolved
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report) => (
                      <tr
                        key={report.id}
                        className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                      >
                        <td className="py-3 px-4 text-sm text-slate-900 dark:text-white">
                          {report.unit?.unit_number ? `Unit ${report.unit.unit_number}` : "Common Area"}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300 capitalize">
                          {report.category?.replace(/_/g, " ") || "—"}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              report.status === "resolved"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                : report.status === "in_progress"
                                ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                : report.status === "acknowledged"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            }`}
                          >
                            {report.status?.replace(/_/g, " ") || "—"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                          {report.created_date ? format(new Date(report.created_date), "MMM d, yyyy") : "N/A"}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                          {report.resolved_date ? format(new Date(report.resolved_date), "MMM d, yyyy") : "—"}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}&from=EstateDashboard`))}
                            className="text-gray-600 dark:text-white border-gray-300 dark:border-white hover:bg-gray-100 dark:hover:bg-white dark:hover:text-slate-900 dark:active:bg-white dark:active:text-slate-900"
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </motion.div>
          </>
        )}

        {/* Internal Issues Tab */}
        {activeTab === "internal" && (
          <>
            {/* Internal Issues Header with New Issue Button */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between"
            >
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Internal Issues</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Manage internal estate maintenance and operations
                </p>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        onClick={() => setShowInternalForm(true)}
                        disabled={!subscription?.hasWriteAccess}
                        className="bg-[#1883FF] hover:bg-[#0066CC] rounded-full sm:rounded-[40px] w-10 h-10 sm:w-auto sm:h-auto p-0 sm:px-4 sm:py-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">New Internal Issue</span>
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {!subscription?.hasWriteAccess && (
                    <TooltipContent>
                      <p>Subscribe to create new internal issues</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </motion.div>

            {/* Internal Issues KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Open Issues</p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                        {internalStatsLoading ? "..." : internalStats?.totalOpen || 0}
                      </p>
                    </div>
                    <div className="p-3 md:p-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                      <AlertCircle className="w-6 h-6 md:w-8 md:h-8 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">New This Month</p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                        {internalStatsLoading ? "..." : internalStats?.newThisMonth || 0}
                      </p>
                    </div>
                    <div className="p-3 md:p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                      <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Completed This Month</p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                        {internalStatsLoading ? "..." : internalStats?.resolvedThisMonth || 0}
                      </p>
                    </div>
                    <div className="p-3 md:p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                      <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Avg. Resolution</p>
                      <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                        {internalStatsLoading ? "..." : `${internalStats?.avgResolutionTime || 0}d`}
                      </p>
                    </div>
                    <div className="p-3 md:p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                      <Clock className="w-6 h-6 md:w-8 md:h-8 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Internal Issues Filters */}
            <Card className="p-6 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Filters</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Status</Label>
                  <Select 
                    value={internalFilters.status} 
                    onValueChange={(value) => setInternalFilters(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger className="dark:border-white dark:text-white">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="reported">New</SelectItem>
                      <SelectItem value="acknowledged">Acknowledged</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="dependent_on">Dependant On</SelectItem>
                      <SelectItem value="pending_approval">Pending Approval</SelectItem>
                      <SelectItem value="resolved">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Category</Label>
                  <Select 
                    value={internalFilters.category} 
                    onValueChange={(value) => setInternalFilters(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger className="dark:border-white dark:text-white">
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      <SelectItem value="electrical">Electrical</SelectItem>
                      <SelectItem value="plumbing">Plumbing</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="general_maintenance">General Maintenance</SelectItem>
                      <SelectItem value="garden_landscape">Garden/Landscape</SelectItem>
                      <SelectItem value="structural">Structural</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Assigned To</Label>
                  <Select 
                    value={internalFilters.assigned_to} 
                    onValueChange={(value) => setInternalFilters(prev => ({ ...prev, assigned_to: value }))}
                  >
                    <SelectTrigger className="dark:border-white dark:text-white">
                      <SelectValue placeholder="All assignments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All assignments</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {contractors.map((contractor) => (
                        <SelectItem key={contractor.id} value={contractor.name}>
                          {contractor.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Start Date</Label>
                  <Input
                    type="date"
                    value={internalFilters.start_date}
                    onChange={(e) => setInternalFilters(prev => ({ ...prev, start_date: e.target.value }))}
                    className="dark:border-white dark:text-white dark:[color-scheme:dark]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">End Date</Label>
                  <Input
                    type="date"
                    value={internalFilters.end_date}
                    onChange={(e) => setInternalFilters(prev => ({ ...prev, end_date: e.target.value }))}
                    className="dark:border-white dark:text-white dark:[color-scheme:dark]"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-slate-600 dark:text-white border-slate-300 dark:border-white dark:hover:bg-white dark:hover:text-slate-900"
                >
                  Clear Filters
                </Button>
              </div>
            </Card>

            {/* Internal Issues List */}
            <Card className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-gray-200 dark:border-slate-700 rounded-xl">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Tag className="w-5 h-5 text-[#1883FF]" />
                    Internal Issues ({internalIssues.length})
                  </h3>
                </div>

                {internalIssuesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1883FF]" />
                  </div>
                ) : internalIssues.length === 0 ? (
                  <div className="text-center py-12">
                    <Settings className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                      No internal issues found
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">
                      Start by creating your first internal issue to track estate maintenance.
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Button
                              onClick={() => setShowInternalForm(true)}
                              disabled={!subscription?.hasWriteAccess}
                              className="bg-[#1883FF] hover:bg-[#0066CC] rounded-[24px] md:rounded-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Create Internal Issue
                            </Button>
                          </div>
                        </TooltipTrigger>
                        {!subscription?.hasWriteAccess && (
                          <TooltipContent>
                            <p>Subscribe to create internal issues</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Title
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Category
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Assigned To
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Created
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {internalIssues.map((issue) => {
                          const dependencyIssue = internalIssues.find(
                            (candidate) => candidate.id === issue.depends_on_issue_id
                          );

                          return (
                          <tr
                            key={issue.id}
                            className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm font-medium text-slate-900 dark:text-white">
                              {issue.title}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300 capitalize">
                              {issue.category?.replace(/_/g, " ")}
                            </td>
                            <td className="py-3 px-4">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  issue.status === "resolved"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                    : issue.status === "in_progress"
                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                                    : issue.status === "dependent_on"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                                    : issue.status === "waiting_contractor"
                                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                                    : issue.status === "acknowledged"
                                    ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                                    : issue.status === "pending_approval"
                                    ? "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400"
                                    : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                }`}
                              >
                                {issue.status === "waiting_contractor"
                                  ? "Waiting on Contractor"
                                  : issue.status === "dependent_on"
                                  ? dependencyIssue
                                    ? `Dependant On: ${dependencyIssue.title}`
                                    : "Dependant On"
                                  : issue.status === "pending_approval"
                                  ? "Pending Approval"
                                  : issue.status.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300 capitalize">
                              {issue.assigned_to || "Unassigned"}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                              {issue.created_date ? format(new Date(issue.created_date), "MMM d, yyyy") : "N/A"}
                            </td>
                            <td className="py-3 px-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleInternalIssueClick(issue)}
                                className="text-gray-600 dark:text-white border-gray-300 dark:border-white hover:bg-gray-100 dark:hover:bg-white dark:hover:text-slate-900 dark:active:bg-white dark:active:text-slate-900"
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>
          </>
        )}

        {/* Contractors Tab */}
        {activeTab === "contractors" && (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg rounded-2xl overflow-hidden">
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#1883FF]" />
                  Contractor Directory
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            onClick={handleImportFromDeviceContacts}
                            disabled={!subscription?.hasWriteAccess}
                            variant="outline"
                            className="rounded-[24px] md:rounded-[40px] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Smartphone className="w-4 h-4" />
                            Import Contacts
                          </Button>
                        </div>
                      </TooltipTrigger>
                      {!subscription?.hasWriteAccess && (
                        <TooltipContent>
                          <p>Subscribe to import contacts</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            onClick={() => {
                              setContractorForm({ name: "", email: "", phone: "", company: "", specialty: "" });
                              setShowContractorModal(true);
                            }}
                            disabled={!subscription?.hasWriteAccess}
                            className="bg-[#1883FF] hover:bg-[#0066CC] text-white rounded-[24px] md:rounded-[40px] flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <UserPlus className="w-4 h-4" />
                            Add Contractor
                          </Button>
                        </div>
                      </TooltipTrigger>
                      {!subscription?.hasWriteAccess && (
                        <TooltipContent>
                          <p>Subscribe to add contractors</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {contractorsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1883FF' }} />
                </div>
              ) : contractors.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                    No contractors yet
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    Add contractors to your directory to quickly assign them to internal issues.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Name
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Company
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Specialty
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Phone
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Email
                        </th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {contractors.map((contractor) => (
                        <tr
                          key={contractor.id}
                          className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
                        >
                          <td className="py-3 px-4 text-sm font-medium text-slate-900 dark:text-white">
                            {contractor.name}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                            {contractor.company || "—"}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300 capitalize">
                            <span className="inline-flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5" />
                              {contractor.specialty || "General"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                            {contractor.phone && (
                              <a
                                href={`tel:${contractor.phone}`}
                                className="inline-flex items-center gap-1 hover:text-[#1883FF]"
                              >
                                <Phone className="w-3.5 h-3.5" />
                                {contractor.phone}
                              </a>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300">
                            {contractor.email && (
                              <a
                                href={`mailto:${contractor.email}`}
                                className="inline-flex items-center gap-1 hover:text-[#1883FF]"
                              >
                                <Mail className="w-3.5 h-3.5" />
                                {contractor.email}
                              </a>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!subscription?.hasWriteAccess}
                                        onClick={() => {
                                          setContractorForm({
                                            id: contractor.id,
                                            name: contractor.name,
                                            email: contractor.email,
                                            phone: contractor.phone,
                                            company: contractor.company,
                                            specialty: contractor.specialty,
                                          });
                                          setShowContractorModal(true);
                                        }}
                                        className="text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-400 dark:hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        Edit
                                      </Button>
                                    </div>
                                  </TooltipTrigger>
                                  {!subscription?.hasWriteAccess && (
                                    <TooltipContent>
                                      <p>Subscribe to edit contractors</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!subscription?.hasWriteAccess}
                                        onClick={() => {
                                          if (!subscription?.hasWriteAccess) {
                                            toast.error("Subscribe to delete contractors");
                                            return;
                                          }
                                          if (window.confirm(`Delete contractor ${contractor.name}?`)) {
                                            deleteContractorMutation.mutate(contractor.id);
                                          }
                                        }}
                                        className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-400 hover:bg-red-50 dark:hover:bg-red-400 dark:hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TooltipTrigger>
                                  {!subscription?.hasWriteAccess && (
                                    <TooltipContent>
                                      <p>Subscribe to delete contractors</p>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Dialogs */}
        <InternalIssueForm
          isOpen={showInternalForm}
          onClose={() => setShowInternalForm(false)}
          estateId={activeEstateId}
        />

        <InternalIssueDetail
          issue={selectedInternalIssue}
          isOpen={!!selectedInternalIssue}
          onClose={() => setSelectedInternalIssue(null)}
          subscription={subscription}
        />

        {/* Import Contractors CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                Import Contractors from CSV
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Import from phone contacts (where supported) or upload a contacts CSV. Duplicate matches are based on phone, then name.
              </p>

              <div className="p-4 border border-dashed border-slate-300 dark:border-slate-600 rounded-xl mb-4">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">CSV File</Label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleCSVFileUpload}
                  className="mt-2"
                  disabled={isImporting}
                />
                {importFileName && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Loaded file: {importFileName}</p>
                )}
              </div>

              {importRows.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-2 mb-3 text-xs">
                    <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">Rows: {importRows.length}</span>
                    <span className="px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                      Duplicates: {importRows.filter((row) => !!row.duplicateContractor).length}
                    </span>
                  </div>

                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-700/50">
                        <tr>
                          <th className="text-left py-2 px-3">Name</th>
                          <th className="text-left py-2 px-3">Phone</th>
                          <th className="text-left py-2 px-3">Email</th>
                          <th className="text-left py-2 px-3">Company</th>
                          <th className="text-left py-2 px-3">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.map((row) => (
                          <tr key={row.id} className="border-t border-slate-100 dark:border-slate-700/50">
                            <td className="py-2 px-3 text-slate-900 dark:text-white">{row.name}</td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{row.phone || "—"}</td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-300 break-all">{row.email}</td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-300">{row.company || "—"}</td>
                            <td className="py-2 px-3">
                              {row.duplicateContractor ? (
                                <Select value={row.action} onValueChange={(value) => handleImportActionChange(row.id, value)}>
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="skip">Skip duplicate</SelectItem>
                                    <SelectItem value="update">Update existing</SelectItem>
                                    <SelectItem value="create">Create new</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="inline-block px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs">
                                  Create
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={resetImportState}
                  variant="outline"
                  className="flex-1 rounded-[24px] md:rounded-[40px]"
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImportContractors}
                  disabled={isImporting || importRows.length === 0}
                  className="flex-1 bg-[#1883FF] hover:bg-[#0066CC] text-white rounded-[24px] md:rounded-[40px] disabled:opacity-50"
                >
                  {isImporting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Import Selected
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Invite Manager Modal */}
        {showInviteModal && inviteData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                Invite a Manager
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Share this link with the person you want to add. It expires in 72 hours and can only be used once.
              </p>

              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 mb-4 flex items-center gap-2">
                <code className="flex-1 text-xs text-slate-700 dark:text-slate-300 break-all select-all">
                  {inviteData.inviteUrl}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-shrink-0 rounded-xl"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteData.inviteUrl);
                    toast.success("Invite link copied!");
                  }}
                >
                  Copy
                </Button>
              </div>

              <p className="text-xs text-slate-400 dark:text-slate-500 mb-5">
                Code: <span className="font-mono font-bold">{inviteData.code}</span>
                {" · "}
                Expires: {new Date(inviteData.expiresAt).toLocaleDateString()}
              </p>

              <Button
                variant="outline"
                className="w-full rounded-[40px]"
                onClick={() => { setShowInviteModal(false); setInviteData(null); }}
              >
                Close
              </Button>
            </motion.div>
          </div>
        )}

        {/* Add/Edit Contractor Modal */}
        {showContractorModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                {contractorForm.id ? "Edit Contractor" : "Add Contractor"}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={contractorForm.name}
                    onChange={(e) => setContractorForm({ ...contractorForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-700 dark:text-white"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={contractorForm.email}
                    onChange={(e) => setContractorForm({ ...contractorForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-700 dark:text-white"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={contractorForm.phone}
                    onChange={(e) => setContractorForm({ ...contractorForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-700 dark:text-white"
                    placeholder="+27 82 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={contractorForm.company}
                    onChange={(e) => setContractorForm({ ...contractorForm, company: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-700 dark:text-white"
                    placeholder="ABC Maintenance"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Specialty
                  </label>
                  <input
                    type="text"
                    value={contractorForm.specialty}
                    onChange={(e) => setContractorForm({ ...contractorForm, specialty: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-slate-700 dark:text-white"
                    placeholder="Plumbing, Electrical, etc."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => {
                    setShowContractorModal(false);
                    setContractorForm({ name: "", email: "", phone: "", company: "", specialty: "" });
                  }}
                  variant="outline"
                  className="flex-1 rounded-[24px] md:rounded-[40px]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    // Check write access
                    if (!subscription?.hasWriteAccess) {
                      toast.error("Subscription required", {
                        description: "Upgrade to add or edit contractors"
                      });
                      return;
                    }

                    if (!contractorForm.name || !contractorForm.email) {
                      toast.error("Name and email are required");
                      return;
                    }
                    if (contractorForm.id) {
                      updateContractorMutation.mutate({
                        id: contractorForm.id,
                        data: {
                          name: contractorForm.name,
                          email: contractorForm.email,
                          phone: contractorForm.phone,
                          company: contractorForm.company,
                          specialty: contractorForm.specialty,
                        },
                      });
                    } else {
                      createContractorMutation.mutate({
                        name: contractorForm.name,
                        email: contractorForm.email,
                        phone: contractorForm.phone,
                        company: contractorForm.company,
                        specialty: contractorForm.specialty,
                      });
                    }
                  }}
                  disabled={!subscription?.hasWriteAccess || createContractorMutation.isPending || updateContractorMutation.isPending}
                  className="flex-1 bg-[#1883FF] hover:bg-[#0066CC] text-white rounded-[24px] md:rounded-[40px] disabled:opacity-50"
                >
                  {(createContractorMutation.isPending || updateContractorMutation.isPending) && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {contractorForm.id ? "Update" : "Add"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
